<template>
  <Tag
    :value="statusConfig.label"
    :class="[statusConfig.class, 'whitespace-nowrap']"
    :severity="statusConfig.severity"
  >
    <span :class="[statusConfig.icon, 'mr-1']"></span>
    {{ statusConfig.label }}
  </Tag>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { calculateDeadlineColor, type DeadlineColor } from '@/utils/ticketDeadline';

import type { TicketStatusBadgeProps } from './ticketStatusBadgeTypes';

const { t } = useI18n();

const props = defineProps<TicketStatusBadgeProps>();

/**
 * Normalize status for display purposes:
 * - NEW → 'new' (show as "Neu"/"New")
 * - IN_TIME, IN_PROGRESS, IN_FOCUS → 'in_progress' (all show as "In Arbeit"/"In Progress")
 * - CLOSED → 'closed'
 * - ALL_OPEN → 'all_open'
 */
const normalizedDisplayStatus = computed(() => {
  const status = props.status.toLowerCase();
  if (status === 'in_time' || status === 'in_focus') {
    return 'in_progress';
  }
  return status;
});

const deadlineColor = computed<DeadlineColor | null>(() => {
  return calculateDeadlineColor(props.deadline);
});

const getSeverityFromColor = (color: DeadlineColor | null): 'success' | 'warn' | 'danger' => {
  if (color === 'yellow') return 'warn';
  if (color === 'red') return 'danger';
  return 'success';
};

const statusConfig = computed(() => {
  const displayStatus = normalizedDisplayStatus.value;
  const color = deadlineColor.value;

  // NEW status
  if (displayStatus === 'new') {
    return {
      label: t('enums.TICKETSTATUS_NEW'),
      icon: 'mdi mdi-star',
      severity: getSeverityFromColor(color),
      class: `status-new-${color || 'default'}`,
    };
  }

  // IN_PROGRESS (from IN_TIME, IN_PROGRESS, or IN_FOCUS)
  if (displayStatus === 'in_progress') {
    return {
      label: t('enums.TICKETSTATUS_IN_PROGRESS'),
      icon: 'mdi mdi-progress-tag',
      severity: getSeverityFromColor(color),
      class: `status-inprogress-${color || 'default'}`,
    };
  }

  // CLOSED
  if (displayStatus === 'closed') {
    return {
      label: t('enums.TICKETSTATUS_CLOSED'),
      icon: 'mdi mdi-tag-check-outline',
      severity: 'info',
      class: 'status-closed',
    };
  }

  // ALL_OPEN (filter value, rarely displayed)
  if (displayStatus === 'all_open') {
    return {
      label: t('enums.TICKETSTATUS_ALL_OPEN'),
      icon: 'mdi mdi-folder-open-outline',
      severity: 'primary',
      class: 'status-all_open',
    };
  }

  // Unknown status fallback
  return {
    label: props.status,
    icon: 'pi pi-question-circle',
    severity: 'secondary',
    class: 'status-unknown',
  };
});
</script>

<style scoped lang="css">
@config "../../../../tailwind.config.js";
@reference "tailwindcss";

/* gemeinsames Badge-Layout */
.status-badge {
  @apply flex items-center gap-1 px-2 py-1;
}

/* NEW status with deadline colors */
.status-new-green {
  @apply text-white border bg-interactiongreen-500 border-interactiongreen-500;
}

.status-new-yellow {
  @apply text-white border bg-signalorange-500 border-signalorange-500;
}

.status-new-red {
  @apply text-white border bg-signalred-500 border-signalred-500;
}

.status-new-default {
  @apply text-white border bg-interactiongreen-500 border-interactiongreen-500;
}

/* IN_PROGRESS status with deadline colors */
.status-inprogress-green {
  @apply text-interactiongreen-500 border border-interactiongreen-500 bg-white;
}

.status-inprogress-yellow {
  @apply text-signalorange-500 border border-signalorange-500 bg-white;
}

.status-inprogress-red {
  @apply text-signalred-500 border border-signalred-500 bg-white;
}

.status-inprogress-default {
  @apply text-interactiongreen-500 border border-interactiongreen-500 bg-white;
}

/* CLOSED status (no color variation) */
.status-closed {
  @apply text-gray-500 border border-gray-500 bg-white;
}

/* ALL_OPEN (filter value) */
.status-all_open {
  @apply text-sky-700 border border-sky-700 bg-white;
}
</style>
